import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styles from '@/styles/BlogForm.module.scss';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from 'axios';
import { Button } from 'react-bootstrap';

const BlogForm = ({type}:any) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  
  const formik = useFormik({
    initialValues: {
      FirstName: '',
      Email: '',
      Company: '',
      Message: '',
      SubmittedFrom: type
    },
    validationSchema: Yup.object({
      FirstName: Yup.string()
    .max(100, 'First Name must be at most 100 characters')
    .required('Full Name is required'),
  Email:Yup.string()
  .trim()
  .max(50, "Email can't be longer than 50 characters")
  .required("Required")
  .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, "Email has an invalid format")
  .test('no-spaces', 'Email should not contain spaces', (value?: string) => !/\s/.test(value || ''))
  .test('no-consecutive-dots', 'Email should not contain consecutive dots', (value?: string) => !/\.\./.test(value || ''))
  .test('no-trailing-dot', 'Email should not end with a dot', (value?: string) => {
    if (!value) return true; // Skip validation if value is empty
    const [localPart] = value.split('@');
    return !localPart.endsWith('.');
  })
  .test('valid-domain', 'Invalid domain format', (value?: string) => {
    if (!value) return true; // Skip validation if value is empty
    const domain = value.split('@')[1];
    if (!domain) return false;
    // Validate domain format with generalized pattern
    return /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/.test(domain);
  })
  .test('no-leading-dot-in-domain', 'Domain should not start with a dot', (value?: string) => {
    if (!value) return true;
    const domain = value.split('@')[1];
    if (!domain) return false;
    return !domain.startsWith('.');
  })
  .test('valid-TLD', 'Top-level domain should be valid', (value?: string) => {
    if (!value) return true;
    const domain = value.split('@')[1];
    if (!domain) return false;
    const tld = domain.split('.').pop(); // Get the TLD (last part of the domain)
    return !!tld && tld.length >= 2 && tld.length <= 63;
  })
  .test('no-XSS-characters', 'Email contains invalid characters', (value?: string) => {
    const xssPattern = /[<>]/;
    return value ? !xssPattern.test(value) : true;
  })
  .test('no-SQL-injection', 'Email contains SQL injection pattern', (value?: string) => {
    if (!value) return true;
    // Allow `xn--` specifically in the domain part of the email
    const [localPart, domainPart] = value.split('@');
    const sqlInjectionPatternLocalPart = /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|ALTER|OR|AND)\b|'|"|;|=|--)/i;
    const sqlInjectionPatternDomainPart = /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|ALTER|OR|AND)\b|'|"|;|=)/i;
    return (
      !sqlInjectionPatternLocalPart.test(localPart) &&
      (!domainPart || !sqlInjectionPatternDomainPart.test(domainPart))
    );
  })
  .test('no-disposable-email', 'Disposable email addresses are not allowed', (value?: string) => {
    if (!value) return true;
    const domain = value.split('@')[1];
    const disposableDomains = [
      "tempmail.com", 
      "10minutemail.com", 
      "guerrillamail.com", 
      "mailinator.com", 
      "yopmail.com"
    ];
    return !disposableDomains.includes(domain);
  }),
  Company: Yup.string()
    .max(100, 'Company Name must be at most 100 characters'),
    // .required('Company Name is required') // Uncomment this if Company Name is required
      Message: Yup.string()
        // .required('Message is required')
        ,
    }),
    onSubmit : async (values: any) => {
      setLoading(true);
      if (values?.phCountryCode) {
        delete values.phCountryCode;
      }

      if (values?.countryName) {
        delete values.countryName;
      }
      Object.keys(values).forEach((k) => {
        if (!values[k]) {
          values[k] = null;
        }
      });
      const trimmedValues = {
        ...values,
        Email: values.Email.trim(),
      };
      await axios
        .post(
          `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/contact-uses`,
          { data: trimmedValues },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_API_TOKEN}`,
            },
          }
        )
        .then((res:any) => {
          setLoading(false);

          if (res?.data?.data?.id) {
            setSuccess(true);
            formik.resetForm();
            // router.push(
            //   `/thank-you?submissionGuid=&submissionGuid=${res?.data?.data?.id}`
            // );
          }
        })
        .catch(() => {
          setSuccess(false)
          setLoading(false);
        });
    },
  });

  return (
    <div className={`${styles.blog} position-sticky`}>
      <div className="">
        <form onSubmit={formik.handleSubmit} className="blog-form-submit p-4">
          <h5>Got a similar project idea?</h5>
          <p>Connect with us & let’s start the journey!</p>

          <div className="mb-3 position-relative">
            <input
              id="FirstName"
              name="FirstName"
              className={`form-control ${formik.touched.FirstName && formik.errors.FirstName ? 'is-invalid' : ''}`}
              placeholder="Full Name*"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.FirstName}
            />
            {formik.touched.FirstName && formik.errors.FirstName ? (
              <div className="invalid-feedback">{formik.errors.FirstName as string}</div>
            ) : null}
          </div>

          <div className="mb-3 position-relative">
            <input
              id="Email"
              name="Email"
              type="email"
              className={`form-control ${formik.touched.Email && formik.errors.Email ? 'is-invalid' : ''}`}
              placeholder="Work Email*"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.Email}
            />
            {formik.touched.Email && formik.errors.Email ? (
              <div className="invalid-feedback">{formik.errors.Email as string}</div>
            ) : null}
          </div>

          <div className="mb-3 position-relative">
            <input
              id="Company"
              name="Company"
              className={`form-control ${formik.touched.Company && formik.errors.Company ? 'is-invalid' : ''}`}
              placeholder="Company Name"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.Company}
            />
          {formik.touched.Email && formik.errors.Company ? (
              <div className="invalid-feedback">{formik.errors.Company as string}</div>
            ) : null} 
          </div>

          <div className="mb-3 position-relative">
            <textarea
              id="Message"
              name="Message"
              className={`form-control `}
              placeholder="Your Message"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.Message}
            />
            
          </div>

          <Button type="submit" className="btn btn-dark">{loading ? "Sending..." : "Send"}</Button>
          {success ? <div style={{border:"1px solid green", padding:"10px",marginTop:"10px", color:"#ffffff", fontSize:"15px"}}>
          We got your message! We'll contact you shortly.
          </div> : null}
        </form>
      </div>
    </div>
  );
};

export default BlogForm;
